import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  UserPlus,
  ClipboardList,
  Video,
  Pill,
  Users,
  Star,
  FileText,
  ArrowRight,
  Check,
  Info,
} from "lucide-react";

const transferSteps = [
  {
    id: 1,
    title: "Check Your Eligibility",
    desc: "Complete our short eligibility questionnaire to confirm you may be suitable. Takes less than 30 seconds.",
    icon: <ShieldCheck className="w-10 h-10 text-white" />,
    color: "bg-gradient-to-br from-[#005eb8] to-[#00aeef]",
  },
  {
    id: 2,
    title: "Create a Patient Account",
    desc: "Register securely on our patient portal to begin your transfer application.",
    icon: <UserPlus className="w-10 h-10 text-white" />,
    color: "bg-gradient-to-br from-[#2d8c6e] to-[#4caf50]",
  },
  {
    id: 3,
    title: "Complete Your Profile",
    desc: "Provide your medical history, current treatment details, and documentation from your existing clinic.",
    icon: <ClipboardList className="w-10 h-10 text-white" />,
    color: "bg-gradient-to-br from-[#2ba78c] to-[#76c042]",
  },
  {
    id: 4,
    title: "Initial Consultation",
    desc: "Attend a secure video appointment with one of our specialist clinicians, who will independently review your case.",
    icon: <Video className="w-10 h-10 text-white" />,
    color: "bg-gradient-to-br from-[#012d5c] to-[#1a2e4a]",
  },
  {
    id: 5,
    title: "MDT Review & Approval",
    desc: "Our multi-disciplinary team reviews your case thoroughly before any prescribing decision is made.",
    icon: <Users className="w-10 h-10 text-white" />,
    color: "bg-gradient-to-br from-[#6b6be0] to-[#8ac2f3]",
  },
  {
    id: 6,
    title: "Prescription Issued",
    desc: "Where clinically approved by the MDT, a prescription is issued and medication is delivered to your door.",
    icon: <Pill className="w-10 h-10 text-white" />,
    color: "bg-gradient-to-br from-[#4caf50] to-[#2d8c6e]",
  },
];

const whyTransfer = [
  {
    icon: <Star className="w-7 h-7 text-[#4caf50]" />,
    title: "Specialist Clinical Team",
    desc: "Our GMC-registered clinicians include specialists in pain management, psychiatry, and neurodevelopmental conditions.",
  },
  {
    icon: <ShieldCheck className="w-7 h-7 text-[#4caf50]" />,
    title: "CQC Registered",
    desc: "Integro Clinics is registered with the Care Quality Commission, providing assurance of safe, high-quality care.",
  },
  {
    icon: <FileText className="w-7 h-7 text-[#4caf50]" />,
    title: "No Consultation Fees",
    desc: "Clinical consultations, follow-up appointments, and repeat prescription reviews are provided at no charge. A £4.99 flat fee applies per prescription, with no other charges.",
  },
  {
    icon: <Video className="w-7 h-7 text-[#4caf50]" />,
    title: "Fully Online",
    desc: "Everything is managed through our secure online portal — from consultations to repeat prescriptions — with no need to travel.",
  },
  {
    icon: <Users className="w-7 h-7 text-[#4caf50]" />,
    title: "Multi-Disciplinary Review",
    desc: "Every prescribing decision is made by an MDT, not a single clinician, ensuring the highest standard of clinical oversight.",
  },
  {
    icon: <ArrowRight className="w-7 h-7 text-[#4caf50]" />,
    title: "Straightforward Transfer Process",
    desc: "Our team handles the administrative side of your transfer and will keep you informed at every step.",
  },
];

const transferFaqs = [
  {
    q: "Do I need to stop my current treatment while transferring?",
    a: "No. Your existing prescription remains with your current provider until your care has formally transferred to Integro. We aim to minimise any interruption to your treatment during this process.",
  },
  {
    q: "Will I automatically receive the same prescription I have now?",
    a: "Not necessarily. Our MDT will independently review your case and make their own clinical assessment. Your current treatment will be taken into consideration, but any prescription issued by Integro will reflect our team's independent clinical judgement.",
  },
  {
    q: "Do I need to tell my existing clinic I am transferring?",
    a: "You are under no obligation to inform your existing clinic in advance. However, you will need to provide documentation or records from them as part of your transfer application. It is often helpful to let them know so they can assist with providing the necessary information.",
  },
  {
    q: "How long does a transfer take?",
    a: "Transfer timescales vary depending on how quickly documentation can be gathered and the complexity of your case. Our team will keep you updated throughout the process and will work to minimise delays.",
  },
  {
    q: "Is there a fee to transfer?",
    a: "There is no charge for transferring your care to Integro. The standard £4.99 flat fee per prescription applies once treatment has been established.",
  },
  {
    q: "What if my condition is not listed on your website?",
    a: "The conditions listed on our website are examples only. Please complete our eligibility check to find out whether your specific situation may be considered. Our clinical team reviews each case individually.",
  },
];

const TransferClinic = () => {
  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero */}
      <div className="relative w-full overflow-hidden flex items-center min-h-[520px]">
        <div
          className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/4031818/pexels-photo-4031818.jpeg?auto=compress&cs=tinysrgb&w=1920&h=900&fit=crop')",
          }}
        >
          <div className="absolute inset-0 bg-[#012d5c]/85"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 py-24 lg:py-32">
          <Reveal>
            <div className="max-w-3xl text-white">
              <span className="inline-block bg-[#4caf50] text-white text-sm font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-6">
                Transfer Clinic
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Transfer Your Care to Integro
              </h1>
              <p className="text-xl lg:text-2xl text-gray-200 mb-8 font-light leading-relaxed max-w-2xl">
                Already receiving medical cannabis treatment elsewhere? Transferring your care to Integro is straightforward. Our team will support you every step of the way.
              </p>
              <Button
                className="bg-[#4caf50] hover:bg-[#3d8b40] text-white text-lg px-10 py-7 rounded-xl shadow-lg"
                onClick={() => window.location.href = "https://app.integroclinics.com/v2/registration"}
              >
                Check Your Eligibility
              </Button>
            </div>
          </Reveal>
        </div>
      </div>

      {/* How Transferring Works */}
      <section className="py-20 bg-[#f0f4f8]">
        <div className="container mx-auto px-4">
          <Reveal>
            <div className="text-center mb-14">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#012d5c] mb-4">
                How Transferring Works
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                The same clear pathway as our standard journey — with additional support for transferring patients.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {transferSteps.map((step) => (
              <Reveal key={step.id}>
                <div className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col border border-gray-100 group">
                  <div className="h-28 flex items-center justify-center relative overflow-hidden bg-[#f8fafc] border-b border-gray-100">
                    <div className={`absolute inset-0 opacity-5 ${step.color} scale-0 group-hover:scale-150 transition-transform duration-500 rounded-full`}></div>
                    <div className={`${step.color} p-4 rounded-2xl shadow-md`}>{step.icon}</div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="shrink-0 w-7 h-7 bg-[#1a2e4a] text-white rounded-full flex items-center justify-center font-bold text-xs">
                        {step.id}
                      </div>
                      <h3 className="text-base font-bold text-[#1a2e4a] leading-tight pt-0.5">{step.title}</h3>
                    </div>
                    <p className="text-gray-500 text-sm leading-relaxed pl-10">{step.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <p className="text-center text-sm text-gray-400 mt-10 max-w-2xl mx-auto">
            Prescription issuance is subject to independent MDT review and clinical approval. A prescription is not guaranteed. Your current treatment at another clinic is not a guarantee of approval at Integro.
          </p>
        </div>
      </section>

      {/* Why Transfer to Integro */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <Reveal>
            <div className="text-center mb-14">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#012d5c] mb-4">
                Why Transfer to Integro?
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Our clinic is built around safe, high-quality, patient-centred care.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {whyTransfer.map((item, i) => (
              <Reveal key={i}>
                <div className="flex gap-5 p-6 bg-[#f8fafc] rounded-xl border border-gray-100 hover:shadow-md transition-shadow h-full">
                  <div className="shrink-0 bg-[#e8f5e9] w-14 h-14 rounded-xl flex items-center justify-center">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="font-bold text-[#012d5c] mb-2">{item.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* What You'll Need */}
      <section className="py-20 bg-[#f4f7fa]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto flex flex-col lg:flex-row gap-12">
            <div className="lg:w-1/2">
              <Reveal>
                <h2 className="text-3xl font-bold text-[#012d5c] mb-6">What You'll Need</h2>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  To make your transfer as smooth as possible, it helps to have the following information ready when you apply.
                </p>
                <ul className="space-y-4">
                  {[
                    "Details of your current diagnosis",
                    "Your current medication and dosage",
                    "A letter or summary from your existing clinic",
                    "Details of previous treatments you have tried",
                    "Relevant medical correspondence or test results",
                    "A valid form of ID",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="shrink-0 bg-[#e8f5e9] p-1.5 rounded-full mt-0.5">
                        <Check className="w-4 h-4 text-[#4caf50]" />
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-sm text-gray-500">
                  Our team will guide you through exactly what is required for your specific case.
                </p>
              </Reveal>
            </div>

            <div className="lg:w-1/2">
              <Reveal delay={200}>
                {/* What Happens to Your Existing Care */}
                <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-100 mb-6">
                  <h3 className="text-xl font-bold text-[#012d5c] mb-4 flex items-center gap-2">
                    <Info className="w-5 h-5 text-[#005eb8]" />
                    What Happens to Your Existing Care?
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Your existing prescription and care remain with your current provider until your transfer to Integro is complete. We aim to minimise any interruption to your treatment.
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    Please note that any prescription issued by Integro reflects an independent clinical assessment by our MDT and may differ from your current prescription. We cannot guarantee continuity of an identical treatment.
                  </p>
                </div>

                {/* Is Transferring Right For Me? */}
                <div className="bg-[#012d5c] p-8 rounded-2xl text-white">
                  <h3 className="text-xl font-bold mb-4">Is Transferring Right for Me?</h3>
                  <p className="text-gray-300 leading-relaxed mb-6">
                    Transferring may be a good option if you are an existing medical cannabis patient who wants to benefit from Integro's clinical team, online platform, zero-fee consultation model, or specialist expertise.
                  </p>
                  <p className="text-gray-300 leading-relaxed mb-6">
                    The best first step is to complete our short eligibility check. This will confirm whether you can proceed with an application.
                  </p>
                  <Button
                    className="w-full bg-[#4caf50] hover:bg-[#3d8b40] text-white py-5 rounded-lg font-bold"
                    onClick={() => window.location.href = "https://app.integroclinics.com/v2/registration"}
                  >
                    Check Your Eligibility
                  </Button>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Transfer-Specific FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <Reveal>
            <h2 className="text-3xl font-bold text-[#012d5c] mb-10 text-center">
              Common Questions About Transferring
            </h2>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <Accordion type="single" collapsible className="w-full">
                {transferFaqs.map((faq, idx) => (
                  <AccordionItem
                    key={idx}
                    value={`transfer-faq-${idx}`}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <AccordionTrigger className="text-base font-semibold text-[#005eb8] hover:text-[#012d5c] text-left px-6 py-5">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 leading-relaxed text-base px-6 pb-5">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
            <p className="text-center mt-6 text-gray-500">
              Have more questions?{" "}
              <Link to="/faq" className="text-[#005eb8] hover:underline font-medium">
                View our full FAQ →
              </Link>
            </p>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-[#4caf50]">
        <div className="container mx-auto px-4 text-center">
          <Reveal>
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to make the move?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed">
              Complete our short eligibility questionnaire to find out whether you may be suitable to transfer your care to Integro Clinics.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                className="bg-white hover:bg-gray-100 text-[#4caf50] font-bold px-10 py-7 rounded-xl text-xl shadow-lg"
                onClick={() => window.location.href = "https://app.integroclinics.com/v2/registration"}
              >
                Check Your Eligibility
              </Button>
              <Button
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-[#4caf50] px-10 py-7 rounded-xl text-xl"
                onClick={() => window.location.href = "mailto:office@integroclinics.com"}
              >
                Contact Us
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TransferClinic;
